<template>
  <AppShell :hero-mode="!isLoggedIn">
    <!-- Guest hero -->
    <PageHero
      v-if="!isLoggedIn"
      eyebrow="Learning Paths"
      icon="fas fa-book"
      title="Course Modules"
      subtitle="Structured paths to take you from fundamentals to advanced exploitation — web, network, crypto and more."
      cta="Start Learning Free"
      cta-href="/register"
      secondary="View Pricing"
      secondary-href="/subscribe"
    />

    <div :class="isLoggedIn ? '' : 'container-app py-10'">
      <!-- Compact heading (authed) -->
      <div v-if="isLoggedIn" class="mb-8">
        <p class="eyebrow">Learning Paths</p>
        <h1 class="mt-2 text-3xl font-extrabold text-white">Course Modules</h1>
        <p class="mt-2 text-ink-muted">Structured paths to take you from fundamentals to advanced exploitation.</p>
      </div>

      <!-- Main layout: sidebar + content -->
      <div class="flex flex-col lg:flex-row gap-8">

        <!-- ── LEFT FILTER SIDEBAR ── -->
        <aside class="lg:w-56 xl:w-64 flex-shrink-0">
          <!-- Mobile: collapsible filter row -->
          <div class="lg:hidden mb-4">
            <button
              @click="showMobileFilters = !showMobileFilters"
              class="btn-secondary w-full justify-between"
            >
              <span><i class="fas fa-filter mr-2"></i>Filters</span>
              <span class="flex items-center gap-2">
                <span
                  v-if="activeFilterCount > 0"
                  class="badge bg-brand text-white"
                >{{ activeFilterCount }}</span>
                <i :class="showMobileFilters ? 'fas fa-chevron-up' : 'fas fa-chevron-down'" class="text-xs"></i>
              </span>
            </button>
          </div>

          <!-- Filter panel (always visible on lg, collapsible on mobile) -->
          <div
            :class="[
              'space-y-6',
              'lg:block',
              showMobileFilters ? 'block' : 'hidden'
            ]"
          >
            <!-- Search -->
            <div>
              <p class="text-xs font-semibold text-ink-faint uppercase tracking-widest mb-3">Search</p>
              <div class="relative">
                <i class="fas fa-search absolute left-3.5 top-1/2 -translate-y-1/2 text-ink-faint text-sm"></i>
                <input v-model="searchQuery" type="text" placeholder="Search modules..." class="input pl-10" />
              </div>
            </div>

            <!-- Status filter -->
            <div>
              <p class="text-xs font-semibold text-ink-faint uppercase tracking-widest mb-3">Status</p>
              <ul class="space-y-1">
                <li v-for="opt in statusOptions" :key="opt.value">
                  <button
                    @click="selectedStatus = opt.value"
                    :class="[
                      'w-full text-left flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors',
                      selectedStatus === opt.value
                        ? 'bg-brand/15 text-brand border border-brand/30 font-semibold'
                        : 'text-ink-muted hover:bg-surface-light hover:text-ink'
                    ]"
                  >
                    <span><i :class="opt.icon" class="mr-2 w-4 text-center"></i>{{ opt.label }}</span>
                    <span class="text-xs text-ink-faint">{{ statusCount(opt.value) }}</span>
                  </button>
                </li>
              </ul>
            </div>

            <!-- Difficulty filter (only shown when difficulty tags exist) -->
            <div v-if="availableDifficulties.length">
              <p class="text-xs font-semibold text-ink-faint uppercase tracking-widest mb-3">Difficulty</p>
              <div class="flex flex-wrap gap-2">
                <button
                  v-for="diff in availableDifficulties"
                  :key="diff"
                  @click="toggleDifficulty(diff)"
                  :class="[
                    'chip transition-colors cursor-pointer',
                    selectedDifficulty === diff
                      ? 'bg-brand/20 text-brand border-brand/40'
                      : 'hover:border-ink-muted hover:text-ink'
                  ]"
                >{{ diff }}</button>
              </div>
            </div>

            <!-- Tag filter -->
            <div v-if="availableTags.length">
              <p class="text-xs font-semibold text-ink-faint uppercase tracking-widest mb-3">Topics</p>
              <div class="flex flex-wrap gap-2">
                <button
                  @click="selectedTag = ''"
                  :class="[
                    'chip transition-colors cursor-pointer',
                    selectedTag === ''
                      ? 'bg-brand/20 text-brand border-brand/40'
                      : 'hover:border-ink-muted hover:text-ink'
                  ]"
                >All</button>
                <button
                  v-for="tag in nonDifficultyTags"
                  :key="tag"
                  @click="selectedTag = selectedTag === tag ? '' : tag"
                  :class="[
                    'chip transition-colors cursor-pointer',
                    selectedTag === tag
                      ? 'bg-brand/20 text-brand border-brand/40'
                      : 'hover:border-ink-muted hover:text-ink'
                  ]"
                >{{ tag }}</button>
              </div>
            </div>

            <!-- Clear filters -->
            <button
              v-if="activeFilterCount > 0"
              @click="clearFilters"
              class="btn-ghost w-full text-sm"
            >
              <i class="fas fa-times mr-1.5"></i>Clear filters
            </button>
          </div>
        </aside>

        <!-- ── RIGHT CONTENT AREA ── -->
        <div class="flex-1 min-w-0">

          <!-- Results bar + view toggle -->
          <div class="flex items-center justify-between mb-5">
            <p class="text-sm text-ink-muted">
              <span class="text-white font-semibold">{{ filteredCourses.length }}</span>
              {{ filteredCourses.length === 1 ? 'module' : 'modules' }} found
            </p>
            <div class="flex items-center gap-1 p-1 rounded-lg bg-surface-light border border-line">
              <button
                @click="viewMode = 'grid'"
                :class="[
                  'p-1.5 rounded-md transition-colors',
                  viewMode === 'grid' ? 'bg-surface-lighter text-white' : 'text-ink-faint hover:text-ink-muted'
                ]"
                title="Grid view"
              >
                <i class="fas fa-grip text-sm"></i>
              </button>
              <button
                @click="viewMode = 'list'"
                :class="[
                  'p-1.5 rounded-md transition-colors',
                  viewMode === 'list' ? 'bg-surface-lighter text-white' : 'text-ink-faint hover:text-ink-muted'
                ]"
                title="List view"
              >
                <i class="fas fa-list text-sm"></i>
              </button>
            </div>
          </div>

          <!-- Featured banner (only when no filters active) -->
          <div v-if="featuredCourse && !activeFilterCount" class="mb-7">
            <p class="eyebrow mb-3"><i class="fas fa-star mr-1"></i>Featured</p>
            <a
              :href="`/modules/${featuredCourse.id}`"
              class="card card-hover group relative overflow-hidden flex flex-col sm:flex-row min-h-[180px] block"
            >
              <!-- Image -->
              <div class="relative sm:w-64 flex-shrink-0 h-44 sm:h-auto overflow-hidden bg-surface-light">
                <img
                  :src="featuredCourse.imageUrl"
                  :alt="featuredCourse.title"
                  class="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  @error="onImgError"
                />
                <div class="absolute inset-0 bg-gradient-to-r from-transparent to-surface sm:block hidden"></div>
                <div class="absolute inset-0 bg-gradient-to-t from-surface via-transparent to-transparent sm:hidden"></div>
              </div>
              <!-- Content -->
              <div class="p-6 flex-1 flex flex-col justify-center">
                <div class="flex flex-wrap gap-2 mb-3">
                  <span v-if="featuredCourse.isExclusive" class="badge bg-yellow-500 text-black">
                    <i class="fas fa-crown"></i> Premium
                  </span>
                  <span v-if="featuredCourse.isCompleted" class="badge bg-success text-black">
                    <i class="fas fa-check"></i> Completed
                  </span>
                  <span v-else-if="featuredCourse.isEnrolled" class="badge bg-info text-black">
                    <i class="fas fa-bookmark"></i> Enrolled
                  </span>
                </div>
                <h2 class="text-xl font-extrabold text-white mb-2">{{ featuredCourse.title }}</h2>
                <p class="text-sm text-ink-muted line-clamp-2 mb-4">{{ featuredCourse.description }}</p>
                <div class="flex flex-wrap gap-1.5 mb-5">
                  <span v-for="(tag, idx) in (featuredCourse.tags || []).slice(0, 4)" :key="idx" class="chip">{{ tag }}</span>
                </div>
                <div>
                  <span class="btn-primary px-5">
                    {{ featuredCourse.isCompleted || featuredCourse.isEnrolled ? 'Course Details' : 'Enroll Now' }}
                    <i class="fas fa-arrow-right text-xs"></i>
                  </span>
                </div>
              </div>
            </a>
          </div>

          <!-- Course list (skip featured when no filters) -->
          <template v-if="displayCourses.length">

            <!-- GRID VIEW -->
            <div
              v-if="viewMode === 'grid'"
              class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5"
            >
              <a
                v-for="(course, index) in displayCourses"
                :key="course.id || index"
                :href="`/modules/${course.id}`"
                class="card card-hover overflow-hidden group flex flex-col"
              >
                <div class="relative h-44 overflow-hidden bg-surface-light">
                  <img
                    :src="course.imageUrl"
                    :alt="course.title"
                    class="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    @error="onImgError"
                  />
                  <div class="absolute inset-0 bg-gradient-to-t from-surface via-transparent to-transparent"></div>
                  <span v-if="course.isExclusive" class="badge absolute top-3 right-3 bg-yellow-500 text-black">
                    <i class="fas fa-crown"></i> Premium
                  </span>
                  <span v-if="course.isCompleted" class="badge absolute top-3 left-3 bg-success text-black">
                    <i class="fas fa-check"></i> Completed
                  </span>
                  <span v-else-if="course.isEnrolled" class="badge absolute top-3 left-3 bg-info text-black">
                    <i class="fas fa-bookmark"></i> Enrolled
                  </span>
                </div>
                <div class="p-5 flex-1 flex flex-col">
                  <h2 class="text-base font-bold text-white mb-1.5 line-clamp-1">{{ course.title }}</h2>
                  <p class="text-sm text-ink-muted line-clamp-2 mb-4 flex-1">{{ course.description }}</p>
                  <div class="flex flex-wrap gap-1.5 mb-4">
                    <span v-for="(tag, idx) in (course.tags || []).slice(0, 3)" :key="idx" class="chip">{{ tag }}</span>
                  </div>
                  <span class="btn-primary w-full justify-center">
                    {{ course.isCompleted || course.isEnrolled ? 'Course Details' : 'Enroll Now' }}
                    <i class="fas fa-arrow-right text-xs"></i>
                  </span>
                </div>
              </a>
            </div>

            <!-- LIST VIEW -->
            <div v-else class="space-y-3">
              <a
                v-for="(course, index) in displayCourses"
                :key="course.id || index"
                :href="`/modules/${course.id}`"
                class="card card-hover overflow-hidden group flex flex-row items-stretch block"
              >
                <!-- Thumbnail -->
                <div class="relative w-32 sm:w-44 flex-shrink-0 overflow-hidden bg-surface-light">
                  <img
                    :src="course.imageUrl"
                    :alt="course.title"
                    class="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    @error="onImgError"
                  />
                  <div class="absolute inset-0 bg-gradient-to-r from-transparent to-surface/40"></div>
                </div>
                <!-- Content -->
                <div class="flex-1 flex flex-col sm:flex-row items-start sm:items-center gap-4 p-4 sm:p-5 min-w-0">
                  <div class="flex-1 min-w-0">
                    <div class="flex flex-wrap gap-1.5 mb-1.5">
                      <span v-if="course.isExclusive" class="badge bg-yellow-500 text-black">
                        <i class="fas fa-crown"></i> Premium
                      </span>
                      <span v-if="course.isCompleted" class="badge bg-success text-black">
                        <i class="fas fa-check"></i> Completed
                      </span>
                      <span v-else-if="course.isEnrolled" class="badge bg-info text-black">
                        <i class="fas fa-bookmark"></i> Enrolled
                      </span>
                    </div>
                    <h2 class="text-sm font-bold text-white line-clamp-1 mb-1">{{ course.title }}</h2>
                    <p class="text-xs text-ink-muted line-clamp-1 mb-2">{{ course.description }}</p>
                    <div class="flex flex-wrap gap-1.5">
                      <span v-for="(tag, idx) in (course.tags || []).slice(0, 3)" :key="idx" class="chip text-[11px] px-2 py-0.5">{{ tag }}</span>
                    </div>
                  </div>
                  <!-- CTA -->
                  <div class="flex-shrink-0">
                    <span class="btn-primary whitespace-nowrap text-xs px-4 py-2">
                      {{ course.isCompleted || course.isEnrolled ? 'Course Details' : 'Enroll Now' }}
                      <i class="fas fa-arrow-right text-xs"></i>
                    </span>
                  </div>
                </div>
              </a>
            </div>

          </template>

          <!-- Empty state -->
          <div v-else-if="!filteredCourses.length" class="card p-12 text-center">
            <i class="fas fa-folder-open text-4xl text-ink-faint mb-4"></i>
            <p class="text-white font-semibold mb-1">No modules found</p>
            <p class="text-sm text-ink-muted mb-5">Try adjusting your filters or search terms.</p>
            <button @click="clearFilters" class="btn-secondary">
              <i class="fas fa-times mr-1.5"></i>Clear all filters
            </button>
          </div>

        </div><!-- /content area -->
      </div><!-- /layout row -->
    </div>
  </AppShell>
</template>

<script>
import AppShell from "@/components/ui-components/AppShell.vue";
import PageHero from "@/components/ui-components/PageHero.vue";
import apiClient from "@/axiosConfig";
import { isAuthenticated } from "@/utils/auth";

const DIFFICULTY_WORDS = ["Beginner", "Intermediate", "Advanced", "Easy", "Medium", "Hard"];

export default {
  name: "ModulesPage",
  components: { AppShell, PageHero },
  data() {
    return {
      // ── Existing fields (unchanged) ──
      searchQuery: "",
      selectedTag: "",
      courses: [],
      // ── New fields ──
      viewMode: "grid",          // "grid" | "list"
      selectedStatus: "all",     // "all" | "enrolled" | "completed"
      selectedDifficulty: "",    // "" | "Beginner" | "Intermediate" | etc.
      showMobileFilters: false,
    };
  },
  computed: {
    // ── Existing computeds (unchanged) ──
    isLoggedIn() {
      return isAuthenticated();
    },
    availableTags() {
      const tags = new Set();
      this.courses.forEach((course) => {
        (course.tags || []).forEach((tag) => tags.add(tag));
      });
      return Array.from(tags);
    },
    filteredCourses() {
      let filtered = this.courses;
      // Existing: search filter
      if (this.searchQuery) {
        const lowerQuery = this.searchQuery.toLowerCase();
        filtered = filtered.filter((course) =>
          course.title.toLowerCase().includes(lowerQuery)
        );
      }
      // Existing: tag filter
      if (this.selectedTag) {
        filtered = filtered.filter((course) =>
          (course.tags || []).includes(this.selectedTag)
        );
      }
      // New: status filter
      if (this.selectedStatus === "enrolled") {
        filtered = filtered.filter((course) => course.isEnrolled && !course.isCompleted);
      } else if (this.selectedStatus === "completed") {
        filtered = filtered.filter((course) => course.isCompleted);
      }
      // New: difficulty filter (derived from tags)
      if (this.selectedDifficulty) {
        filtered = filtered.filter((course) =>
          (course.tags || []).includes(this.selectedDifficulty)
        );
      }
      return filtered;
    },

    // ── New computeds ──
    availableDifficulties() {
      const found = new Set();
      this.courses.forEach((course) => {
        (course.tags || []).forEach((tag) => {
          if (DIFFICULTY_WORDS.includes(tag)) found.add(tag);
        });
      });
      // Return in canonical order
      return DIFFICULTY_WORDS.filter((d) => found.has(d));
    },
    nonDifficultyTags() {
      return this.availableTags.filter((t) => !DIFFICULTY_WORDS.includes(t));
    },
    statusOptions() {
      return [
        { value: "all",       label: "All Modules", icon: "fas fa-layer-group" },
        { value: "enrolled",  label: "Enrolled",    icon: "fas fa-bookmark" },
        { value: "completed", label: "Completed",   icon: "fas fa-check-circle" },
      ];
    },
    activeFilterCount() {
      let count = 0;
      if (this.searchQuery) count++;
      if (this.selectedTag) count++;
      if (this.selectedStatus !== "all") count++;
      if (this.selectedDifficulty) count++;
      return count;
    },
    featuredCourse() {
      if (!this.courses.length) return null;
      // Prefer first enrolled or exclusive, otherwise first course
      return (
        this.courses.find((c) => c.isEnrolled || c.isExclusive) ||
        this.courses[0]
      );
    },
    // Courses shown in the grid/list (skip featured when no filters)
    displayCourses() {
      if (!this.activeFilterCount && this.featuredCourse) {
        return this.filteredCourses.filter((c) => c.id !== this.featuredCourse.id);
      }
      return this.filteredCourses;
    },
  },
  methods: {
    // ── Existing methods (unchanged) ──
    async fetchModules() {
      try {
        const response = await apiClient.get("/modules");
        const baseUrl = process.env.VUE_APP_BASE_URL;
        this.courses = response.data.map((course) => ({
          ...course,
          imageUrl: `${baseUrl}/static/modules/banners/${course.id}`,
        }));
      } catch (error) {
        console.error("Error fetching modules:", error);
      }
    },
    onImgError(e) {
      e.target.src = require("@/assets/logo.png");
    },

    // ── New methods ──
    toggleDifficulty(diff) {
      this.selectedDifficulty = this.selectedDifficulty === diff ? "" : diff;
    },
    clearFilters() {
      this.searchQuery = "";
      this.selectedTag = "";
      this.selectedStatus = "all";
      this.selectedDifficulty = "";
    },
    statusCount(value) {
      if (value === "all") return this.courses.length;
      if (value === "enrolled") return this.courses.filter((c) => c.isEnrolled && !c.isCompleted).length;
      if (value === "completed") return this.courses.filter((c) => c.isCompleted).length;
      return 0;
    },
  },
  mounted() {
    this.fetchModules();
  },
};
</script>
